import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NCSubtype, NCType, NCSubtypeSearchParams } from '../../services/ncsubtypeApi';

// State interfaces
export interface NCSubtypeState {
  // Data
  items: NCSubtype[];
  selectedItem: NCSubtype | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Active: boolean;
    NCTypeId: number;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: NCSubtypeSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
    byNCType: { [key: number]: number };
  };
  
  // NCType Data
  ncTypes: NCType[];
  ncTypesLoading: boolean;
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: NCSubtypeState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Active: true,
    NCTypeId: 0,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    ncTypeId: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
    byNCType: {},
  },
  
  // NCType Data
  ncTypes: [],
  ncTypesLoading: false,
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const ncsubtypeSlice = createSlice({
  name: 'ncsubtype',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<NCSubtype[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<NCSubtype | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<NCSubtype>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<NCSubtype>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<NCSubtypeState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
      // Clear validation errors when form data changes
      if (state.validationErrors.length > 0) {
        state.validationErrors = [];
      }
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
      state.validationErrors = [];
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<NCSubtypeSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<NCSubtypeState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<NCSubtypeState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // NCType management
    setNCTypes: (state, action: PayloadAction<NCType[]>) => {
      state.ncTypes = action.payload;
    },
    setNCTypesLoading: (state, action: PayloadAction<boolean>) => {
      state.ncTypesLoading = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetNCSubtypeState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setNCTypes,
  setNCTypesLoading,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetNCSubtypeState,
} = ncsubtypeSlice.actions;

// Saga action types
export const FETCH_NCSUBTYPES = 'ncsubtype/fetchNCSubtypes';
export const FETCH_NCSUBTYPE_BY_ID = 'ncsubtype/fetchNCSubtypeById';
export const CREATE_NCSUBTYPE = 'ncsubtype/createNCSubtype';
export const UPDATE_NCSUBTYPE = 'ncsubtype/updateNCSubtype';
export const DELETE_NCSUBTYPE = 'ncsubtype/deleteNCSubtype';
export const SEARCH_NCSUBTYPES = 'ncsubtype/searchNCSubtypes';
export const FETCH_STATISTICS = 'ncsubtype/fetchStatistics';
export const FETCH_NCTYPES = 'ncsubtype/fetchNCTypes';
export const FETCH_BY_NCTYPE_ID = 'ncsubtype/fetchByNCTypeId';
export const BULK_ACTIVATE = 'ncsubtype/bulkActivate';
export const BULK_DEACTIVATE = 'ncsubtype/bulkDeactivate';
export const BULK_DELETE = 'ncsubtype/bulkDelete';
export const EXPORT_TO_CSV = 'ncsubtype/exportToCSV';

// Saga action creators
export const fetchNCSubtypes = (params?: NCSubtypeSearchParams) => ({ type: FETCH_NCSUBTYPES, payload: params });
export const fetchNCSubtypeById = (id: number) => ({ type: FETCH_NCSUBTYPE_BY_ID, payload: id });
export const createNCSubtype = (ncsubtype: Omit<NCSubtype, 'Id'>) => ({ type: CREATE_NCSUBTYPE, payload: ncsubtype });
export const updateNCSubtype = (id: number, ncsubtype: Partial<NCSubtype>) => ({ type: UPDATE_NCSUBTYPE, payload: { id, ncsubtype } });
export const deleteNCSubtype = (id: number) => ({ type: DELETE_NCSUBTYPE, payload: id });
export const searchNCSubtypes = (params: NCSubtypeSearchParams) => ({ type: SEARCH_NCSUBTYPES, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const fetchNCTypes = () => ({ type: FETCH_NCTYPES });
export const fetchByNCTypeId = (ncTypeId: number) => ({ type: FETCH_BY_NCTYPE_ID, payload: ncTypeId });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: NCSubtypeSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default ncsubtypeSlice.reducer; 